import { oc } from "@orpc/contract";
import { z } from "zod";

const UUIDSchema = z.uuid();

/**
 * Email Draft Status Schema
 */
export const EmailDraftStatusSchema = z.enum([
  "draft",
  "sending",
  "sent",
  "discarded",
]);

/**
 * Email Draft Schema
 */
export const EmailDraftSchema = z.object({
  id: UUIDSchema,
  organizationId: UUIDSchema,
  inboundEmailId: UUIDSchema.nullable().optional(),
  createdByUserId: UUIDSchema.nullable().optional(),
  sendingMailbox: z.string(),
  to: z.string(),
  cc: z.string().nullable().optional(),
  bcc: z.string().nullable().optional(),
  subject: z.string(),
  content: z.string(),
  htmlContent: z.string().nullable().optional(),
  conversationId: z.string().nullable().optional(),
  inReplyToMessageId: z.string().nullable().optional(),
  internetMessageId: z.string().nullable().optional(),
  isAiGenerated: z.boolean(),
  aiOriginalContent: z.string().nullable().optional(),
  aiOriginalHtmlContent: z.string().nullable().optional(),
  modifiedByUserId: UUIDSchema.nullable().optional(),
  modifiedAt: z.coerce.date().nullable().optional(),
  status: EmailDraftStatusSchema,
  topic: z.string().nullable().optional(),
  outboundEmailId: UUIDSchema.nullable().optional(),
  createdAt: z.coerce.date(),
  updatedAt: z.coerce.date(),
});

/**
 * Get Email Draft Input Schema
 */
export const GetEmailDraftInputSchema = z.object({
  id: UUIDSchema,
});

/**
 * Update Email Draft Input Schema
 */
export const UpdateEmailDraftInputSchema = z.object({
  id: UUIDSchema,
  subject: z.string().optional(),
  htmlContent: z.string().optional(),
  to: z.array(z.string().email()).optional(),
  cc: z.array(z.string().email()).optional(),
  bcc: z.array(z.string().email()).optional(),
});

/**
 * Send Email Draft Input Schema
 */
export const SendEmailDraftInputSchema = z.object({
  id: UUIDSchema,
});

/**
 * Send Email Draft Output Schema
 */
export const SendEmailDraftOutputSchema = z.object({
  draft: EmailDraftSchema,
  outboundEmailId: UUIDSchema,
  messageId: z.string().nullable(),
  conversationId: z.string().nullable().optional(),
});

/**
 * Discard Email Draft Input Schema
 */
export const DiscardEmailDraftInputSchema = z.object({
  id: UUIDSchema,
});

/**
 * Email Draft Contracts
 */
export const emailDraftContracts = {
  // Get an email draft by ID
  get: oc.input(GetEmailDraftInputSchema).output(EmailDraftSchema).route({
    method: "GET",
    path: "/email-drafts/{id}",
  }),

  // Update an email draft
  update: oc.input(UpdateEmailDraftInputSchema).output(EmailDraftSchema).route({
    method: "PATCH",
    path: "/email-drafts/{id}",
  }),

  // Send an email draft
  send: oc
    .input(SendEmailDraftInputSchema)
    .output(SendEmailDraftOutputSchema)
    .route({
      method: "POST",
      path: "/email-drafts/{id}/send",
    }),

  // Discard an email draft
  discard: oc
    .input(DiscardEmailDraftInputSchema)
    .output(EmailDraftSchema)
    .route({
      method: "POST",
      path: "/email-drafts/{id}/discard",
    }),
} as const;

// Export types
export type EmailDraftStatus = z.infer<typeof EmailDraftStatusSchema>;
export type EmailDraft = z.infer<typeof EmailDraftSchema>;
export type GetEmailDraftInput = z.infer<typeof GetEmailDraftInputSchema>;
export type UpdateEmailDraftInput = z.infer<typeof UpdateEmailDraftInputSchema>;
export type SendEmailDraftInput = z.infer<typeof SendEmailDraftInputSchema>;
export type SendEmailDraftOutput = z.infer<typeof SendEmailDraftOutputSchema>;
export type DiscardEmailDraftInput = z.infer<
  typeof DiscardEmailDraftInputSchema
>;
